import { type SortOrder, compareTimestamp } from '~/models/tableSort'
import type { WorkflowCaseRow } from '~/pages/WorkflowCases/models/workflowCase'

/**
 * 一覧のソート列 (spec AC-WFW-001: 進捗 / 対象日 / 最終更新)。
 */
export type SortBy = 'progress' | 'targetDate' | 'updatedAt'

export type { SortOrder }

/** デフォルトソート (spec AC-WFW-001: 最終更新の降順)。 */
export const DEFAULT_SORT_BY: SortBy = 'updatedAt'
export const DEFAULT_SORT_ORDER: SortOrder = 'desc'

function compareWorkflowId(a: WorkflowCaseRow, b: WorkflowCaseRow): number {
  return a.workflowId.localeCompare(b.workflowId)
}

/**
 * ワークフローケース一覧を指定列でソートする純関数
 * (spec AC-WFW-001 / design §5「ソート比較関数」)。
 *
 * 比較値が同値の場合、 workflow_id 昇順で tie-break する。
 * `updatedAt` / `targetDate` が undefined の行は sortOrder に関わらず末尾に配置する。
 * 呼び出し元 (index.tsx) は client-side フィルタ適用後の行配列に対して本関数を適用する
 * (design §4「ソート実装方式: client-side ソート」)。
 */
export function sortWorkflowCases(
  rows: WorkflowCaseRow[],
  sortBy: SortBy,
  sortOrder: SortOrder,
): WorkflowCaseRow[] {
  return [...rows].sort((a, b) => {
    let diff = 0
    switch (sortBy) {
      case 'progress': {
        const totalA = a.progress.total
        const totalB = b.progress.total
        const rateA = totalA > 0 ? a.progress.done / totalA : 0
        const rateB = totalB > 0 ? b.progress.done / totalB : 0
        diff = rateA - rateB
        break
      }
      case 'targetDate': {
        if (a.targetDate === undefined && b.targetDate === undefined) {
          diff = 0
        } else if (a.targetDate === undefined) {
          return 1
        } else if (b.targetDate === undefined) {
          return -1
        } else {
          const dateA = new Date(
            a.targetDate.year,
            a.targetDate.month - 1,
            a.targetDate.day,
          )
          const dateB = new Date(
            b.targetDate.year,
            b.targetDate.month - 1,
            b.targetDate.day,
          )
          diff = dateA.getTime() - dateB.getTime()
        }
        break
      }
      case 'updatedAt':
      default: {
        if (a.updatedAt === undefined && b.updatedAt === undefined) {
          diff = 0
        } else if (a.updatedAt === undefined) {
          return 1
        } else if (b.updatedAt === undefined) {
          return -1
        } else {
          diff = compareTimestamp(a.updatedAt, b.updatedAt)
        }
      }
    }
    if (diff === 0) {
      // tie-break は sortOrder に関わらず workflow_id 昇順で固定する。
      return compareWorkflowId(a, b)
    }
    return sortOrder === 'asc' ? diff : -diff
  })
}
